import { Assumption, AssumptionCategoryBasic } from '@portal/pkg-api/gen/navigator'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AssumptionQueries } from '@portal/pkg-filter-criteria/data'
import { CategorizedAssumptionPickerProps } from '..'

type UseFilteredDataProps = CategorizedAssumptionPickerProps['filters']

type UseFilteredDataReturn = {
  assumptions: ReadonlyArray<Assumption>
  categories: ReadonlyArray<AssumptionCategoryBasic>
}

export function useFilteredData(filters: UseFilteredDataProps = {}): UseFilteredDataReturn {
  const { data: categories = [] } = useQuery(AssumptionQueries.categories())
  const { data: assumptions = [] } = useQuery(AssumptionQueries.assumptions(filters.entityTypeId))
  const filteredAssumptions = useMemo(() => {
    let filteredAssumptions: ReadonlyArray<Assumption> = assumptions
    if (filters.onlyFilterable) {
      filteredAssumptions = assumptions.filter((a) => a.filterable)
    } else if (filters.includedAssumptionTypes) {
      filteredAssumptions = assumptions.filter((a) =>
        filters.includedAssumptionTypes!.includes(a.type),
      )
    } else if (filters.excludedAssumptionTypes) {
      filteredAssumptions = assumptions.filter(
        (a) => !filters.excludedAssumptionTypes!.includes(a.type),
      )
    }

    if (filters.excludePseudo) {
      filteredAssumptions = filteredAssumptions.filter((a) => !a.pseudo)
    }

    if (filters.predicate) {
      filteredAssumptions = filteredAssumptions.filter(filters.predicate)
    }

    return filteredAssumptions.toSorted((a, b) => (a.categoryId ?? -1) - (b.categoryId ?? -1))
  }, [
    assumptions,
    filters.excludePseudo,
    filters.excludedAssumptionTypes,
    filters.includedAssumptionTypes,
    filters.onlyFilterable,
    filters.predicate,
  ])
  const filteredCategories = useMemo(() => {
    const categoryIds = new Set(filteredAssumptions.map((a) => a.categoryId))
    return categories.filter((c) => categoryIds.has(c.id))
  }, [categories, filteredAssumptions])

  return { assumptions: filteredAssumptions, categories: filteredCategories }
}
